import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Compass, GraduationCap, Handshake, Network } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Button from './ui/Button';
import Card from './ui/Card';
import SectionHeader from './ui/SectionHeader';

const founders = [
  {
    name: 'Syed Mohammed Ibrahim Ali',
    image: '/101.jpeg',
    role: 'Co-founder',
  },
  {
    name: 'Syed Moaaz Ahmed',
    image: '/102.jpeg',
    role: 'Co-founder',
  },
];

const principles = [
  {
    icon: Handshake,
    title: 'Bridge the gap',
    copy: 'Create direct, respectful pathways between visually impaired candidates and employers.',
    color: 'from-blue-500 to-cyan-400',
  },
  {
    icon: GraduationCap,
    title: 'Build employability',
    copy: 'Develop future skill-up programs around technology, soft skills, and industry readiness.',
    color: 'from-teal-500 to-emerald-400',
  },
  {
    icon: Network,
    title: 'Grow community',
    copy: 'Support mentorship, collaboration, and a stronger professional network over time.',
    color: 'from-amber-400 to-rose-400',
  },
];

function AboutUs() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Clear Horizons - About Us';
  }, []);

  const handleJoinMission = () => {
    navigate('/home', { state: { scrollToBottom: true } });
  };

  return (
    <main id="main-content" tabIndex="-1" className="page-shell focus:outline-none">
      <section className="container-page pb-16 pt-8">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="mx-auto max-w-4xl text-center"
        >
          <p className="eyebrow">About Clear Horizons</p>
          <h1 className="mt-4 text-5xl font-semibold tracking-tight text-slate-950 sm:text-6xl">
            Illuminating paths and <span className="gradient-text">redefining possibilities.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-slate-600">
            Clear Horizons exists to help blind and visually impaired individuals showcase their
            capabilities, connect with employers, and move toward meaningful employment.
          </p>
        </motion.div>
      </section>

      <section className="container-page pb-20">
        <div className="grid gap-4 md:grid-cols-2">
          {founders.map((founder, index) => (
            <motion.div
              key={founder.name}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: index * 0.08 }}
            >
              <Card className="group h-full overflow-hidden p-1 transition duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-slate-950/10">
                <div className="h-2 rounded-t-2xl bg-gradient-to-r from-blue-500 via-teal-400 to-amber-400" />
                <div className="p-5">
                <div className="flex items-center gap-5">
                  <img
                    src={founder.image}
                    alt={founder.name}
                    className="h-24 w-24 rounded-2xl object-cover ring-1 ring-slate-200"
                  />
                  <div>
                    <p className="text-sm font-medium text-blue-600">{founder.role}</p>
                    <h2 className="mt-1 text-xl font-semibold leading-tight text-slate-950">
                      {founder.name}
                    </h2>
                  </div>
                </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        <Card className="mt-6 overflow-hidden p-8 sm:p-10">
          <div className="-mx-10 -mt-10 mb-10 h-2 color-strip" />
          <div className="grid gap-8 lg:grid-cols-[0.75fr_1.25fr] lg:items-start">
            <div>
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-50 via-teal-50 to-amber-50 text-blue-600">
                <Compass className="h-6 w-6" />
              </div>
              <h2 className="mt-5 text-2xl font-semibold tracking-tight text-slate-950">
                The founding motive
              </h2>
            </div>
            <p className="text-base leading-8 text-slate-600">
              Syed Mohammed Ibrahim Ali and Syed Moaaz Ahmed envisioned Clear Horizons as a
              platform that bridges the gap between talented blind individuals and employers.
              Their mission is to create an inclusive future where every person has the chance to
              thrive, contribute, and be recognized for their skills.
            </p>
          </div>
        </Card>
      </section>

      <section className="surface-grid border-y border-slate-200/70 bg-white/70 py-16">
        <div className="container-page">
          <SectionHeader eyebrow="What comes next" title="A movement built for long-term impact" align="center">
            <p>
              The product direction supports a future with skill-building, community programs, and
              employer partnerships.
            </p>
          </SectionHeader>

          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {principles.map((principle, index) => {
              const Icon = principle.icon;
              return (
                <motion.div
                  key={principle.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: index * 0.08 }}
                >
                <Card className="h-full overflow-hidden p-6 transition duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-slate-950/10">
                  <div className={`-mx-6 -mt-6 mb-6 h-2 bg-gradient-to-r ${principle.color}`} />
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-950 text-white">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 text-lg font-semibold text-slate-950">{principle.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-slate-600">{principle.copy}</p>
                </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="container-page py-20">
        <Card className="animated-border bg-white p-1 text-center">
          <div className="rounded-[calc(1rem-1px)] bg-white p-8 sm:p-10">
          <p className="mx-auto max-w-2xl text-xl font-medium leading-8 text-slate-800">
            Together, let&apos;s create a world where everyone has a clear path to opportunity.
          </p>
          <div className="mt-7 flex justify-center">
            <Button as="button" type="button" size="lg" variant="color" onClick={handleJoinMission}>
              Join our mission <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
          </div>
        </Card>
      </section>
    </main>
  );
}

export default AboutUs;
